import { FetchQueryOptions, QueryClient } from "@tanstack/react-query";
import type {
  ParamsForMethod,
  ReturnForMethod,
  StandardQueryMethodForModel,
  ModelName,
  TellescopeQueryKey,
  SupportedSession,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { executeTellescopeApiMethod } from "../execute-tellescope-api-method";
import { TellescopeDefinedQueryOptionKeys } from "../../hooks/types";

type TellescopeFetchQueryOptions<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> = Omit<
  FetchQueryOptions<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>,
  TellescopeDefinedQueryOptionKeys
>;

export interface FetchTellescopeQueryArgs<
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
> extends TellescopeFetchQueryOptions<Model, Method, TError, TData> {
  model: Model;
  method: Method;
  request: ParamsForMethod<Model, Method>;
  queryClient: QueryClient;
  session: SupportedSession;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

export const fetchTellescopeQuery = <
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  queryClient,
  session,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: FetchTellescopeQueryArgs<Model, Method, TError, TData>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey });
  return queryClient.fetchQuery<ReturnForMethod<Model, Method>, TError, TData, TellescopeQueryKey<Model, Method>>({
    queryKey,
    queryFn: () =>
      executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
    ...tanstackOptions,
  });
};
